// Coworker thread route — unified with Conversations
// (docs/COWORKER_CONVERSATION_UNIFICATION.md): threads render at
// /c/:conversationId with inline artifacts + SSE live updates. This route
// only resolves the linked conversation and redirects. Threads without a
// link (pre-unification, un-backfilled) are no longer viewable here; the
// backfill script links every task.
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import { useAuthStore, useCoworkerStore } from '@/store';
import { openHomeComposer } from '@/lib/openHomeComposer';

export default function CoworkerThreadRedirectRoute() {
  const { threadId: threadIdParam } = useLocalSearchParams();
  const threadId = Array.isArray(threadIdParam) ? threadIdParam[0] : threadIdParam;
  const router = useRouter();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const hasUser = useAuthStore((s) => !!s.user);
  const fetchTask = useCoworkerStore((s) => s.fetchTask);

  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!threadId || !hasUser) { setIsResolved(true); return; }
    (async () => {
      const task = await fetchTask(threadId);
      if (cancelled) return;
      if (task?.conversationId) {
        router.replace(`/c/${task.conversationId}`);
      } else {
        setIsResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [threadId, hasUser, fetchTask, router]);

  if (!isResolved) {
    return (
      <>
        <MetaHead title="Work — Gab" />
        <View style={[styles.container, { paddingTop: insets.top + 24, alignItems: 'center' }]}>
          <ActivityIndicator color={colors.accent_color_primary} />
        </View>
      </>
    );
  }

  return (
    <>
      <MetaHead title="Work — Gab" />
      <View style={[styles.container, { paddingTop: insets.top + 16, paddingHorizontal: 16, gap: 12 }]}>
        <Text size="n" color="secondary">
          {hasUser
            ? 'This work is not available. It may have been deleted or failed to link.'
            : 'Sign in to view Work threads.'}
        </Text>
        <Button text="Back to Work" size="small" onPress={() => openHomeComposer(router, { work: true })} />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-start',
  },
});
